import React, { useState, FunctionComponent, useEffect, useCallback } from 'react';
import { getUrlParams, Office, useCities, useOffices } from '../../../utils';
import { Input } from '../Input';
import { useForm } from 'react-hook-form';
import { Button } from '../Button';

interface FormItems {
  searchTerm: string;
}

const formItems: FormItems = {
  searchTerm: 'searchTerm',
};

interface OfficeSelectorProps {
  setValue?: (value: Office | null) => void;
  office?: Office | null;
}

export const OfficeSelector: FunctionComponent<OfficeSelectorProps> = ({ setValue, office}) => {

  const {
    register,
    formState: {errors},
    watch,
  } = useForm({mode: 'onChange'});

  const {
    offices,
    cities,
    fetchOffice,
    fetchCities,
    setOffices,
    setCities,
    error,
    loading,
  } = useCities();

  // Wrap fetchOfficeById in useCallback to stabilize it
  const { fetchOfficeById } = useOffices();
  const fetchOfficeByIdMemoized = useCallback(fetchOfficeById, []);

  const values: Record<any, any> = watch();
  const [isSearchTermValid, setIsSearchTermValid] = useState(false);
  const [selectedOffice, setSelectedOffice] = useState<Office | null>(null);

  const search = async (event: any) => {
    await fetchCities(values.searchTerm);
  }

  const selectCity = async (city: string) => {
    setCities([]);
    await fetchOffice(city);
  }

  const selectOffice = useCallback(async (office: Office | null) => {
    setValue && setValue(office);
    setSelectedOffice(office);
    setOffices([]);
  }, [setValue, setOffices]);

  useEffect(() => {
    const officeId = getUrlParams('officeid') || null;
    if (officeId) {
      fetchOfficeByIdMemoized(officeId).then((res) => {
        if (res) {
          selectOffice(res);
        }
      });
    }
  }, [selectOffice, fetchOfficeByIdMemoized]); // Now fetchOfficeByIdMemoized is stable

  useEffect(() => {
    setValue && setValue(null);
  }, [setValue]);

  useEffect(() => {
    if (office) {
      selectOffice(office);
    }
  }, [office, selectOffice]);

  useEffect(() => {
    setIsSearchTermValid(values.searchTerm ? true : false);
  }, [values]);

  useEffect(() => {
    if (offices.length === 1) {
      selectOffice(offices[0]);
    }
  }, [offices, selectOffice]);

  return (
    <div className={`c-office-selector`}>
      {!selectedOffice && (
      <>
        <div className={`c-office-selector__input`}>
          <Input
            {...{
              label: 'Postcode of plaats*',
              helperLabel: 'Zoek naar een vestiging waar je een afspraak wil maken',
              placeholder: '',
              name: formItems.searchTerm,
              id: formItems.searchTerm,
              register: register,
              hasError: errors[formItems.searchTerm] || error,
              validation: {required: true},
            }}
          />
          <div className={`c-office-selector__submit`}>
            <Button isLoading={loading} onClick={search} label="Zoeken" type={`button`} disabled={!isSearchTermValid}/>
          </div>
        </div>
        {error && (
          <span className="form__error">
                {error}
            </span>
        )}
        <div>

          {cities.map((city) => (
            <div key={city}>
              <Button onClick={() => selectCity(city)} label={city} type={`button`} variant={'link'}/>
            </div>
          ))}
        </div>
        <div>
          {offices.length > 1 && (
            <div className={`c-office-selector__office-result-count`}> <b>{offices.length}</b> vestigingen gevonden bij jou in de buurt</div>
          )}
          <div>
            {offices.map((office) => (
              <div key={office.officeId}>
                <Button onClick={() => selectOffice(office)} label={office.fullName} type={`button`} variant={'link'}/>
              </div>
            ))}
          </div>
        </div>
      </>
      )}
      {selectedOffice && (
      <>
        <div className="c-input c-office-selector__selection">
          <label className="input__label" htmlFor="email">Gekozen vestiging*</label>
          <input className="input__input input__disabled" type="text" id="selected-office" name="selected-office" autoComplete="off" disabled={true} value={selectedOffice.fullName}/>
        </div>
        <div>
          <Button type={`button`} onClick={() => selectOffice(null)} label="Wijzig vestiging" variant={'link'}/>
        </div>
      </>
      )}
    </div>
  )
    ;
}
